import {
  Accordion,
  Chip,
  Group,
  Input,
  MultiSelect,
  Select,
  Stack,
  Text,
  Textarea,
} from "@assetwatch/ui";
import type { ReactNode } from "react";
import type { AssetId } from "../../../lib/workIds";
import type { WorkOrderDetail } from "../../../queries/useWorkOrderDetailQuery";
import type { useWorkOrderLookupsQuery } from "../../../queries/useWorkOrderLookupsQuery";
import { EquipmentLocationPicker } from "../requests/EquipmentLocationPicker";
import { isSelectableStatusOption } from "./presentation";
import {
  DetailCard,
  DetailGrid,
  Field,
  formatDate,
} from "./WorkOrderDetailLayout";
import { WorkOrderOverview } from "./WorkOrderOverview";

export type EditDraft = {
  version: string;
  title: string;
  /** The selected Asset or Location record, or null for none. */
  assetId: AssetId | null;
  /** Sorted ids of the shared Categories on the Work Order. */
  categoryIds: number[];
  description: string;
  instructions: string;
  priorityCode: string | null;
  statusCode: string;
  workTypeCode: string;
  plannerGroupCode: string | null;
  startDate: string;
  dueDate: string;
  assignees: string[];
  procedureId: string | null;
};

export function findEditDraftConflicts(
  draft: EditDraft,
  baseline: EditDraft,
  latest: EditDraft,
): Array<keyof EditDraft> {
  return (Object.keys(draft) as Array<keyof EditDraft>).filter(
    (key) =>
      key !== "version" &&
      JSON.stringify(draft[key]) !== JSON.stringify(baseline[key]) &&
      JSON.stringify(latest[key]) !== JSON.stringify(baseline[key]) &&
      JSON.stringify(draft[key]) !== JSON.stringify(latest[key]),
  );
}

/** Rebase after the user explicitly chooses how to resolve concurrent edits. */
export function rebaseEditDraft(
  draft: EditDraft,
  baseline: EditDraft,
  latest: EditDraft,
  resolution: "mine" | "latest",
): EditDraft {
  const rebased = { ...latest };
  const conflicts = new Set(findEditDraftConflicts(draft, baseline, latest));
  for (const key of Object.keys(draft) as Array<keyof EditDraft>) {
    if (key === "version") continue;
    if (resolution === "latest" && conflicts.has(key)) continue;
    if (JSON.stringify(draft[key]) !== JSON.stringify(baseline[key])) {
      Object.assign(rebased, { [key]: draft[key] });
    }
  }
  return rebased;
}

/**
 * Converts a stored UTC ISO timestamp into the local-wall-clock string a
 * `datetime-local` input expects ("YYYY-MM-DDTHH:mm"). `datetime-local`
 * inputs have no timezone of their own — the browser treats whatever string
 * is in the DOM value as the viewer's local time, and `EditDetailsForm`'s
 * save path (`new Date(editDraft.startDate).toISOString()`) parses it back
 * the same way. If this function instead sliced the UTC string's digits
 * as-is, the displayed value would silently be UTC-labeled-as-local: in any
 * non-UTC browser, re-saving the field unchanged would shift the stored
 * instant by the browser's offset. Subtracting the timezone offset before
 * slicing makes the round trip (load -> display -> save with no edits)
 * lossless in every timezone.
 */
export function toEditableDate(value: string | null) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.valueOf())) return "";
  const localDate = new Date(
    date.getTime() - date.getTimezoneOffset() * 60_000,
  );
  return localDate.toISOString().slice(0, 16);
}

/**
 * Maps a Work Order's schedule to `EditDraft`'s `startDate`/`dueDate` pair.
 * Shared by both `toEditDraft` implementations (WorkOrderSplitView.tsx and
 * WorkOrderDetailScreen.tsx) so this mapping only needs to change in one
 * place — not a full dedup of those two otherwise-separate functions.
 */
export function toScheduleEditDraft(schedule: {
  basicStartDate: string | null;
  dueDate: string | null;
}): Pick<EditDraft, "startDate" | "dueDate"> {
  return {
    startDate: toEditableDate(schedule.basicStartDate),
    dueDate: toEditableDate(schedule.dueDate),
  };
}

/**
 * Maps a Work Order's Title, Asset and Categories to the matching draft
 * fields. Shared by both `toEditDraft` implementations for the same reason as
 * `toScheduleEditDraft`.
 */
export function toPlanningEditDraft(
  workOrder: Pick<WorkOrderDetail, "title" | "asset" | "categories">,
): Pick<EditDraft, "title" | "assetId" | "categoryIds"> {
  return {
    title: workOrder.title,
    assetId: workOrder.asset?.id ?? null,
    categoryIds: workOrder.categories
      .map((category) => category.id)
      .sort((first, second) => first - second),
  };
}

/** The Title, Asset and Categories as the update request carries them. */
export function planningUpdateFields(draft: EditDraft) {
  return {
    title: draft.title.trim(),
    assetId: draft.assetId,
    categoryIds: draft.categoryIds,
  };
}

/**
 * Whether the Work Order's Title, Asset and Categories are locked: Done and
 * Canceled Work Orders must be reopened first, and the API refuses a change.
 */
export function isPlanningReadOnly(statusCode: string) {
  return statusCode === "COMPLETED" || statusCode === "CANCELED";
}

/** The message shown under a blank Title, which blocks saving. */
export function editDraftTitleError(draft: EditDraft) {
  return draft.title.trim() === "" ? "Enter a title." : undefined;
}

/**
 * Whether the draft changes anything the field update writes, compared with a
 * draft built from the saved Work Order. Assignees and version are excluded so
 * an assignment-only edit does not bump the Work Order version. Without edit
 * access to the fields, only the status is written.
 */
export function editDraftFieldsChanged(
  draft: EditDraft,
  saved: EditDraft,
  canEditFields: boolean,
  includeProcedure = true,
): boolean {
  if (draft.statusCode !== saved.statusCode) return true;
  if (!canEditFields) return false;
  return (
    draft.title.trim() !== saved.title.trim() ||
    draft.assetId !== saved.assetId ||
    draft.categoryIds.join(",") !== saved.categoryIds.join(",") ||
    draft.description !== saved.description ||
    draft.instructions !== saved.instructions ||
    draft.priorityCode !== saved.priorityCode ||
    draft.workTypeCode !== saved.workTypeCode ||
    draft.plannerGroupCode !== saved.plannerGroupCode ||
    draft.startDate !== saved.startDate ||
    draft.dueDate !== saved.dueDate ||
    (includeProcedure && draft.procedureId !== saved.procedureId)
  );
}

export function EditDetailsForm({
  draft,
  lookupData,
  onChange,
  reopenOnly = false,
  planningReadOnly = false,
  assigneesField,
  allowedStatusCodes,
  descriptionActions,
  workOrder,
  attachments,
}: {
  workOrder: WorkOrderDetail;
  attachments?: ReactNode;
  descriptionActions?: ReactNode;
  draft: EditDraft;
  lookupData: ReturnType<typeof useWorkOrderLookupsQuery>["data"];
  onChange: (update: Partial<EditDraft>) => void;
  reopenOnly?: boolean;
  /**
   * The Work Order is Done or Canceled: Title, Asset and Categories are shown
   * but cannot change until it is reopened.
   */
  planningReadOnly?: boolean;
  /** The "Assigned to" field, shown full width in Status & Schedule. */
  assigneesField?: ReactNode;
  /**
   * Status moves the caller may make from the Work Order's saved status. When
   * given, the Status select offers only those and the saved status, so a
   * caller restricted to the standard transitions is not offered a move the
   * server will refuse.
   */
  allowedStatusCodes?: { savedCode: string; targets: string[] };
}) {
  const selectableStatuses =
    lookupData?.statuses.filter((item) =>
      isSelectableStatusOption(item.code, draft.statusCode),
    ) ?? [];
  const statusOptions = selectableStatuses
    .filter(
      (item) =>
        !allowedStatusCodes ||
        item.code === allowedStatusCodes.savedCode ||
        allowedStatusCodes.targets.includes(item.code),
    )
    .map((item) => ({ label: item.name, value: item.code }));
  if (reopenOnly) {
    return (
      <Stack>
        <Text tone="subtle">
          A completed Work Order must be reopened before its details can be
          edited.
        </Text>
        <WorkOrderOverview
          workOrder={workOrder}
          descriptionActions={descriptionActions}
          attachments={attachments}
          assigneesField={assigneesField}
          statusControl={
            <Select
              label="Status"
              onChange={(value) => value && onChange({ statusCode: value })}
              options={selectableStatuses.map((item) => ({
                label: item.name,
                value: item.code,
              }))}
              value={draft.statusCode}
            />
          }
        />
      </Stack>
    );
  }
  // The active Categories the facility offers, plus any Category already on
  // the Work Order that has since been retired, so it still shows as a chip
  // and stays until the user removes it.
  const offeredCategoryIds = new Set(
    workOrder.categoryOptions.map((category) => category.id),
  );
  const categoryOptions = [
    ...workOrder.categoryOptions.map((category) => ({
      label: category.name,
      value: String(category.id),
    })),
    ...workOrder.categories
      .filter((category) => !offeredCategoryIds.has(category.id))
      .map((category) => ({
        label: `${category.name} (inactive)`,
        value: String(category.id),
      })),
  ];
  const savedAssetId = workOrder.asset?.id ?? null;
  return (
    <Stack>
      <DetailCard id="details" title="Description" actions={descriptionActions}>
        <div className="flex flex-col gap-(--spacing-16) px-(--spacing-20) py-(--spacing-20)">
          {planningReadOnly ? null : (
            // @ds-deviation: the POC Work Order detail panel has no title edit (the title caption sits above Description only in the new-work-order draft) — proposed resolution: fix in the POC by adding a Title field to the detail edit mode
            <Input
              error={editDraftTitleError(draft)}
              label="Title"
              onChange={(event) => onChange({ title: event.target.value })}
              required
              value={draft.title}
            />
          )}
          <Textarea
            aria-label="Description"
            onChange={(event) => onChange({ description: event.target.value })}
            value={draft.description}
          />
        </div>
      </DetailCard>
      <DetailCard title="Status & Schedule">
        <DetailGrid>
          <Select
            label="Status"
            onChange={(value) => value && onChange({ statusCode: value })}
            options={statusOptions}
            value={draft.statusCode}
          />
          <Select
            label="Priority"
            onChange={(value) => onChange({ priorityCode: value })}
            options={
              lookupData?.priorities.map((item) => ({
                label: item.name,
                value: item.code,
              })) ?? []
            }
            value={draft.priorityCode}
          />
          <Input
            label="Start date"
            onChange={(event) => onChange({ startDate: event.target.value })}
            type="datetime-local"
            value={draft.startDate}
          />
          <Input
            label="Due date"
            onChange={(event) => onChange({ dueDate: event.target.value })}
            type="datetime-local"
            value={draft.dueDate}
          />
        </DetailGrid>
        {assigneesField ? (
          <div className="border-t border-outline-subtle px-(--spacing-20) py-(--spacing-20)">
            {assigneesField}
          </div>
        ) : null}
      </DetailCard>
      <DetailCard title="Work Information">
        <DetailGrid>
          {planningReadOnly ? (
            <>
              <Field
                label="Asset"
                value={workOrder.asset?.name || workOrder.asset?.code}
              />
              <Field label="Location" value={workOrder.asset?.location} />
              <Field label="Categories">
                {workOrder.categories.length > 0 ? (
                  <Group gap={8} wrap="wrap">
                    {workOrder.categories.map((category) => (
                      <Chip
                        key={category.id}
                        label={
                          category.active
                            ? category.name
                            : `${category.name} (inactive)`
                        }
                      />
                    ))}
                  </Group>
                ) : (
                  <Text>—</Text>
                )}
              </Field>
            </>
          ) : (
            <>
              <div className="min-w-0 sm:col-span-2 lg:col-span-3">
                <EquipmentLocationPicker
                  fallbackName={
                    workOrder.asset && draft.assetId === savedAssetId
                      ? workOrder.asset.name || workOrder.asset.code
                      : null
                  }
                  label="Asset or location"
                  onChange={(assetId) => onChange({ assetId })}
                  value={draft.assetId}
                />
              </div>
              {draft.assetId === savedAssetId ? (
                <Field label="Location" value={workOrder.asset?.location} />
              ) : (
                <Field label="Location">
                  <Text tone="subtle">Updates when the asset is saved.</Text>
                </Field>
              )}
              <div className="sm:col-span-1 lg:col-span-2">
                {/* @ds-deviation: the DS MultiSelect chip remove control is labelled just "Remove", so a screen reader cannot tell which Category it removes — proposed resolution: promote into the design system (a remove label that includes the option label) */}
                {/* @ds-deviation: a Category retired after it was added stays on the Work Order and is shown as "Name (inactive)" in the option label, because the DS MultiSelect option has no secondary status marker — proposed resolution: promote into the design system (option description or status adornment) */}
                <MultiSelect
                  label="Categories"
                  onChange={(values) =>
                    onChange({
                      categoryIds: values
                        .map(Number)
                        .sort((first, second) => first - second),
                    })
                  }
                  options={categoryOptions}
                  placeholder="Choose categories"
                  value={draft.categoryIds.map(String)}
                />
              </div>
            </>
          )}
        </DetailGrid>
        <div className="border-t border-outline-subtle">
          <Accordion title="More Info">
            <div className="grid grid-cols-1 gap-x-(--spacing-32) gap-y-(--spacing-20) sm:grid-cols-2 lg:grid-cols-3">
              <Select
                label="Work type"
                onChange={(value) => value && onChange({ workTypeCode: value })}
                options={
                  lookupData?.workOrderTypes.map((item) => ({
                    label: item.name,
                    value: item.code,
                  })) ?? []
                }
                value={draft.workTypeCode}
              />
              <Select
                label="Planner group"
                searchable
                onChange={(value) => onChange({ plannerGroupCode: value })}
                options={
                  lookupData?.plannerGroups.map((item) => ({
                    label: item.name,
                    value: item.code,
                  })) ?? []
                }
                value={draft.plannerGroupCode}
              />
              <Field
                label="Asset classification"
                value={workOrder.asset?.category}
              />
              <Field
                label="Equipment code"
                value={workOrder.metadata.equipmentCode}
              />
              <Field
                label="Work center"
                value={workOrder.metadata.catalogProfile}
              />
              <Field
                label="Created"
                value={
                  (workOrder.lifecycle.createdBy || "—") +
                  " · " +
                  formatDate(workOrder.lifecycle.createdOn)
                }
              />
              <Field
                label="Last updated"
                value={formatDate(workOrder.lifecycle.updatedOn)}
              />
              <div className="sm:col-span-2 lg:col-span-3">
                <Textarea
                  label="Instructions"
                  onChange={(event) =>
                    onChange({ instructions: event.target.value })
                  }
                  value={draft.instructions}
                />
              </div>
            </div>
          </Accordion>
        </div>
        {attachments ? (
          <div className="border-t border-outline-subtle px-(--spacing-20) py-(--spacing-20)">
            {attachments}
          </div>
        ) : null}
      </DetailCard>
    </Stack>
  );
}
